import { useEffect, useRef, useState } from 'react';
import type { VerseEnv } from '@rb/shared/admin-verses';
import type { EventConfig } from '@rb/shared/event-config';
import type { R2Flavor } from '@rb/shared/r2-flavor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  emptyAdminConfig,
  fetchEventConfigFlavored,
  publishEventConfigFlavored,
} from '@/master-data/lib/event-config';
import { useVerseEnvChannel } from '@/verse/use-verse-env-channel';

/**
 * JSON 편집용 textarea. raw 텍스트를 로컬 state 로 보관해 편집 중 일시적
 * invalid JSON 입력(배열 원소 추가, 중괄호 삭제 등)을 허용한다. parse 성공
 * 시에만 onChange 로 상위 config 를 갱신하고, 실패하면 에러를 표면화한다.
 * (master-data 의 JsonCell 과 동일 패턴 — controlled value 직접 바인딩 금지)
 */
function JsonEditor({
  value,
  onChange,
  className,
  ariaLabel,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  className?: string;
  ariaLabel?: string;
}) {
  const initial = JSON.stringify(value, null, 2);
  const [text, setText] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  // 직전에 onChange 로 상위에 올려보낸 parse 결과를 보관. 이 값이 그대로
  // value 로 되돌아오면(=사용자 본인 편집의 라운드트립) textarea 를 재포맷하지
  // 않는다. 외부 요인(채널 변경 등)으로 value 가 바뀌었을 때만 동기화한다.
  // raw 텍스트가 아닌 parse 결과(canonical 직렬화)와 비교해야 정확하다.
  const lastEmitted = useRef<string>(initial);

  useEffect(() => {
    const incoming = JSON.stringify(value, null, 2);
    // 사용자 편집이 라운드트립으로 돌아온 경우엔 재포맷하지 않는다(커서 튐 방지).
    if (incoming === lastEmitted.current) return;
    setText(incoming);
    lastEmitted.current = incoming;
    setErr(null);
  }, [value]);

  return (
    <div className="flex flex-col gap-1">
      <Textarea
        className={className}
        value={text}
        aria-label={ariaLabel}
        aria-invalid={err != null}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          try {
            const parsed = JSON.parse(next);
            setErr(null);
            // 올려보내는 값의 canonical 직렬화를 기록. 이 편집이 라운드트립으로
            // 돌아와도 useEffect 가 동일 값으로 보고 재포맷을 건너뛴다.
            lastEmitted.current = JSON.stringify(parsed, null, 2);
            onChange(parsed);
          } catch (parseErr) {
            setErr((parseErr as Error).message);
          }
        }}
      />
      {err && <span className="text-xs text-destructive">JSON: {err}</span>}
    </div>
  );
}

const CHANNELS = ['dev', 'staging', 'prod'] as const;
const CONDITION_KEYS = [
  'STAGE_CLEAR',
  'VALHALLA_WAVE_REACH',
  'TOTAL_STAR_COUNT',
  'PET_OWNED',
  'PET_SUMMON_COUNT',
] as const;
const FLAVORS: R2Flavor[] = ['web2', 'web3'];

/** epoch ms → datetime-local 입력값("YYYY-MM-DDTHH:mm", 로컬). 미설정이면 "". */
function msToDatetimeLocal(ms: number | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** datetime-local 입력값 → epoch ms. 빈 값이면 undefined. */
function datetimeLocalToMs(v: string): number | undefined {
  if (!v) return undefined;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

function emptyForms(): { web2: EventConfig; web3: EventConfig } {
  return { web2: emptyAdminConfig(), web3: emptyAdminConfig() };
}

export default function EventManagementPage() {
  const { channel, setChannel, verseEnv } = useVerseEnvChannel();
  const [flavor, setFlavor] = useState<R2Flavor>('web3');
  const [forms, setForms] = useState<{ web2: EventConfig; web3: EventConfig }>(emptyForms);
  const [status, setStatus] = useState<string>('');
  // R2 로드 성공 여부 — false 면 발행 차단 (로드 실패 시 empty forms 로 sibling flavor 덮어쓰기 방지)
  const [loaded, setLoaded] = useState(false);

  // 현재 flavor 의 폼 config 편의 접근자
  const config = forms[flavor];
  const setConfig = (updater: (prev: EventConfig) => EventConfig) =>
    setForms((p) => ({ ...p, [flavor]: updater(p[flavor]) }));

  useEffect(() => {
    setLoaded(false);
    void (async () => {
      setStatus('불러오는 중…');
      try {
        setForms(await fetchEventConfigFlavored(channel));
        setStatus('');
        setLoaded(true);
      } catch (e) {
        setStatus(`불러오기 실패: ${String(e)}`);
      }
    })();
  }, [channel]);

  const onPublish = async () => {
    // R2 로드가 완료되지 않은 상태에서 발행하면 empty forms 로 sibling flavor 가 덮어써진다
    if (!loaded) return;
    // production 채널은 한 번의 클릭으로 라이브 이벤트 설정(할인·동반자·마일스톤)을
    // 즉시 덮어쓰므로, 파괴적 쓰기 관례에 맞춰 확인 가드를 둔다.
    if (
      channel === 'prod' &&
      !window.confirm('prod 채널의 라이브 이벤트 설정을 덮어씁니다. 계속하시겠습니까?')
    ) {
      return;
    }
    setStatus('발행 중…');
    try {
      await publishEventConfigFlavored(channel, forms);
      setStatus('발행 완료');
    } catch (e) {
      setStatus(`발행 실패: ${String(e)}`);
    }
  };

  return (
    <section className="flex flex-col gap-6 p-6">
      {/* TODO(l10n): WS-D 에서 하드코딩 한국어 → useT() 키 전환 */}
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold">이벤트 관리</h1>
      </header>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="event-channel">채널</Label>
        <Select value={channel} onValueChange={(v) => setChannel(v as VerseEnv)}>
          <SelectTrigger id="event-channel" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {verseEnv && (
          <span className="text-xs text-muted-foreground">
            활성 verse 환경({verseEnv})에 자동 동기화됩니다 · 수동 변경 가능
          </span>
        )}
      </div>

      {/* flavor 토글 (web2 | web3) */}
      <div className="flex items-center gap-2">
        <Label>모드</Label>
        <fieldset className="flex rounded-md border m-0 p-0 border-0">
          {FLAVORS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={flavor === f}
              className={`px-4 py-1.5 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                flavor === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-muted text-muted-foreground'
              }`}
              onClick={() => setFlavor(f)}
            >
              {f}
            </button>
          ))}
        </fieldset>
        <span className="text-xs text-muted-foreground">
          발행 시 web2/web3 양쪽 동시 저장됩니다
        </span>
      </div>

      {/* 할인 토글 */}
      <Card>
        <CardHeader>
          <CardTitle>할인 라벨</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="discount-upgrade-gold"
              checked={config.discount.upgradeGold}
              onCheckedChange={(v) =>
                setConfig((c) => ({
                  ...c,
                  discount: { ...c.discount, upgradeGold: v === true },
                }))
              }
            />
            <Label htmlFor="discount-upgrade-gold">강화 골드 할인 라벨</Label>
            <Label htmlFor="discount-upgrade-gold-pct" className="ml-2">
              %
            </Label>
            <Input
              id="discount-upgrade-gold-pct"
              type="number"
              className="w-20"
              value={config.discount.upgradeGoldPct}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  discount: { ...c.discount, upgradeGoldPct: Number(e.target.value) },
                }))
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="discount-upgrade-ruby"
              checked={config.discount.upgradeRuby}
              onCheckedChange={(v) =>
                setConfig((c) => ({
                  ...c,
                  discount: { ...c.discount, upgradeRuby: v === true },
                }))
              }
            />
            <Label htmlFor="discount-upgrade-ruby">강화 루비 할인 라벨</Label>
            <Label htmlFor="discount-upgrade-ruby-pct" className="ml-2">
              %
            </Label>
            <Input
              id="discount-upgrade-ruby-pct"
              type="number"
              className="w-20"
              value={config.discount.upgradeRubyPct}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  discount: { ...c.discount, upgradeRubyPct: Number(e.target.value) },
                }))
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="discount-pet-sapphire"
              checked={config.discount.petSapphire}
              onCheckedChange={(v) =>
                setConfig((c) => ({
                  ...c,
                  discount: { ...c.discount, petSapphire: v === true },
                }))
              }
            />
            <Label htmlFor="discount-pet-sapphire">펫 사파이어 할인 라벨</Label>
            <Label htmlFor="discount-pet-sapphire-pct" className="ml-2">
              %
            </Label>
            <Input
              id="discount-pet-sapphire-pct"
              type="number"
              className="w-20"
              value={config.discount.petSapphirePct}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  discount: { ...c.discount, petSapphirePct: Number(e.target.value) },
                }))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* 동반자: active/goal + 티어 JSON 편집(MVP) */}
      <Card>
        <CardHeader>
          <CardTitle>신들의 동반자</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="companion-active"
              checked={config.companion.active}
              onCheckedChange={(v) =>
                setConfig((c) => ({ ...c, companion: { ...c.companion, active: v === true } }))
              }
            />
            <Label htmlFor="companion-active">활성</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="companion-goal">목표($)</Label>
            <Input
              id="companion-goal"
              type="number"
              className="w-32"
              value={config.companion.goal}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  companion: { ...c.companion, goal: Number(e.target.value) },
                }))
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="companion-end">종료 일시</Label>
            <Input
              id="companion-end"
              type="datetime-local"
              className="w-56"
              value={msToDatetimeLocal(config.companion.endTime)}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  companion: { ...c.companion, endTime: datetimeLocalToMs(e.target.value) },
                }))
              }
            />
          </div>
          <JsonEditor
            className="h-40 w-full whitespace-pre-wrap break-all font-mono text-xs"
            ariaLabel="동반자 티어 JSON"
            value={config.companion.tiers}
            onChange={(tiers) =>
              setConfig((c) => ({
                ...c,
                companion: { ...c.companion, tiers: tiers as EventConfig['companion']['tiers'] },
              }))
            }
          />
        </CardContent>
      </Card>

      {/* 마일스톤: active + items JSON 편집(조건키 드롭다운은 후속) */}
      <Card>
        <CardHeader>
          <CardTitle>발할라로 가는 길</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="milestones-active"
              checked={config.milestones.active}
              onCheckedChange={(v) =>
                setConfig((c) => ({
                  ...c,
                  milestones: { ...c.milestones, active: v === true },
                }))
              }
            />
            <Label htmlFor="milestones-active">활성</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="milestone-end">종료 일시</Label>
            <Input
              id="milestone-end"
              type="datetime-local"
              className="w-56"
              value={msToDatetimeLocal(config.milestones.endTime)}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  milestones: { ...c.milestones, endTime: datetimeLocalToMs(e.target.value) },
                }))
              }
            />
          </div>
          <p className="text-xs text-muted-foreground">조건키: {CONDITION_KEYS.join(', ')}</p>
          <JsonEditor
            className="h-48 w-full whitespace-pre-wrap break-all font-mono text-xs"
            ariaLabel="마일스톤 items JSON"
            value={config.milestones.items}
            onChange={(items) =>
              setConfig((c) => ({
                ...c,
                milestones: { ...c.milestones, items: items as EventConfig['milestones']['items'] },
              }))
            }
          />
        </CardContent>
      </Card>

      {/* 별모으기(초기 보상): active + 종료 시각. 단계·보상은 마스터데이터 퀘스트(isEvent) */}
      <Card>
        <CardHeader>
          <CardTitle>별모으기 (초기 보상)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="star-reward-active"
              checked={config.starReward.active}
              onCheckedChange={(v) =>
                setConfig((c) => ({ ...c, starReward: { ...c.starReward, active: v === true } }))
              }
            />
            <Label htmlFor="star-reward-active">활성</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="star-reward-end">종료 일시</Label>
            <Input
              id="star-reward-end"
              type="datetime-local"
              className="w-56"
              value={msToDatetimeLocal(config.starReward.endTime)}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  starReward: { ...c.starReward, endTime: datetimeLocalToMs(e.target.value) },
                }))
              }
            />
          </div>
          <p className="text-xs text-muted-foreground">
            단계·별 개수·보상은 마스터데이터 이벤트 퀘스트(isEvent)에서 관리됩니다.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button type="button" onClick={onPublish} disabled={!loaded}>
          R2 발행
        </Button>
        {status && <p className="text-sm text-muted-foreground">{status}</p>}
      </div>
    </section>
  );
}
